/**
 * 🏢 AdminApprovalsList - Listagem de Solicitações de Aprovação (REFATORADO)
 * 
 * Padrão seguindo a tela de Tickets: botões de filtro, busca separada, StatCard
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Badge, Form, Row, Col, Spinner, Alert, Modal, InputGroup, Table } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import AdminPageHeader from '../common/AdminPageHeader';
import StatCard from '../common/StatCard';
import ApprovalFilters from './ApprovalFilters';
import { Drawer } from '../../theme/components';
import { API_URL } from '../../config/api';

const AdminApprovalsList = () => {
  // Estados principais
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado de abas (Pendentes, Aprovadas, Rejeitadas)
  const [activeView, setActiveView] = useState('pending');

  // Estados de filtros e paginação
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Estados de Drawer e Modais
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Estados de formulários
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');

  // Mapear activeView para status da API
  const getStatusFilter = () => {
    switch (activeView) {
      case 'pending':
        return 'pending_validation';
      case 'approved':
        return 'approved';
      case 'rejected':
        return 'rejected';
      default:
        return 'all';
    }
  };

  // Carregar dados quando mudar filtros, paginação ou aba
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadRequests();
      loadStats();
    }
  }, [filters, pagination.page, activeView]);

  // Carregar lista de solicitações
  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const statusFilter = getStatusFilter();

      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter,
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to }),
        ...(searchTerm && { search: searchTerm })
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/approvals?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao carregar solicitações');
      }

      if (data.success) {
        // Se estiver na aba "pending", filtrar também pending_approval
        let filteredRequests = data.data.requests;
        if (activeView === 'pending') {
          filteredRequests = filteredRequests.filter(r => 
            r.status === 'pending_validation' || r.status === 'pending_approval'
          );
        }
        
        setRequests(filteredRequests);
        setPagination(prev => ({
          ...prev,
          ...data.data.pagination
        }));
      } else {
        throw new Error(data.message || 'Erro ao carregar solicitações');
      }

    } catch (error) {
      console.error('❌ Erro ao carregar solicitações:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Carregar estatísticas
  const loadStats = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/approvals/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar estatísticas:', error);
    }
  };

  // Atualizar filtros
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({
      date_from: '',
      date_to: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Mudar aba
  const handleViewChange = (view) => {
    setActiveView(view);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Navegar páginas
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Abrir Drawer de detalhes
  const handleViewRequest = async (request) => {
    try {
      setLoadingDetails(true);
      setSelectedRequest(null);
      setShowDetailsDrawer(true);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/approvals/${request.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSelectedRequest(data.data);
      } else {
        throw new Error(data.message || 'Erro ao carregar detalhes da solicitação');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar detalhes:', error);
      setError(error.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Abrir modal de aprovação
  const handleApproveRequest = (request) => {
    setSelectedRequest({ responsible: request });
    setApprovalNotes('');
    setShowApprovalModal(true);
  };

  // Confirmar aprovação
  const handleConfirmApproval = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/approvals/${selectedRequest.responsible.id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notes: approvalNotes
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowApprovalModal(false);
        setShowDetailsDrawer(false);
        loadRequests();
        loadStats();
      } else {
        throw new Error(data.message || 'Erro ao aprovar responsável');
      }
    } catch (error) {
      console.error('❌ Erro ao aprovar:', error);
      setError(error.message);
    }
  };

  // Abrir modal de reprovação
  const handleRejectRequest = (request) => {
    setSelectedRequest({ responsible: request });
    setRejectionReason('');
    setRejectionNotes('');
    setShowRejectionModal(true);
  };

  // Confirmar reprovação
  const handleConfirmRejection = async () => {
    if (!rejectionReason.trim() || rejectionReason.trim().length < 10) {
      setError('Motivo da reprovação é obrigatório (mínimo 10 caracteres)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/approvals/${selectedRequest.responsible.id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: rejectionReason,
          notes: rejectionNotes
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowRejectionModal(false);
        setShowDetailsDrawer(false);
        loadRequests();
        loadStats();
      } else {
        throw new Error(data.message || 'Erro ao reprovar responsável');
      }
    } catch (error) {
      console.error('❌ Erro ao reprovar:', error);
      setError(error.message);
    }
  };

  // Função para obter badge de status
  const getStatusBadge = (status) => {
    const variants = {
      pending_validation: 'warning',
      pending_approval: 'info',
      approved: 'success',
      rejected: 'danger'
    };
    const labels = {
      pending_validation: 'Aguardando Validação',
      pending_approval: 'Aguardando Aprovação',
      approved: 'Aprovado',
      rejected: 'Rejeitado'
    };
    return (
      <Badge bg={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Função para formatar CNPJ
  const formatCNPJ = (cnpj) => {
    if (!cnpj) return 'N/A';
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  // Função para formatar CPF
  const formatCPF = (cpf) => {
    if (!cpf) return 'N/A';
    return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  };

  // Preparar estatísticas para as abas
  const tabStats = useMemo(() => {
    if (!stats) return { pending: 0, approved: 0, rejected: 0 };
    return {
      pending: (stats.pendingValidation || 0) + (stats.pendingApproval || 0),
      approved: stats.approved || 0,
      rejected: stats.rejected || 0
    };
  }, [stats]);

  // Filtrar requests por busca
  const filteredRequests = useMemo(() => {
    if (!searchTerm) return requests;
    const term = searchTerm.toLowerCase();
    return requests.filter(r => 
      (r.trading_name && r.trading_name.toLowerCase().includes(term)) ||
      (r.company_name && r.company_name.toLowerCase().includes(term)) ||
      (r.user_name && r.user_name.toLowerCase().includes(term)) ||
      (r.user_email && r.user_email.toLowerCase().includes(term)) ||
      (r.state_registration && r.state_registration.includes(term))
    );
  }, [requests, searchTerm]);

  return (
    <div className="admin-approvals-list">
      {/* Cabeçalho */}
      <div className="d-flex justify-content-between align-items-start" style={{ marginBottom: '24px' }}>
        <AdminPageHeader
          icon="solar:user-check-rounded-bold"
          title="Aprovação de Responsável"
          subtitle="Gerencie as aprovações de responsáveis legais"
        />
        <Button variant="outline-primary" onClick={loadRequests} disabled={loading}>
          <Icon icon="mdi:refresh" className="me-2" />
          Atualizar
        </Button>
      </div>

      {/* Estatísticas - StatCard padronizado */}
      {stats && (
        <Row style={{ marginBottom: '24px' }}>
          <Col md={3}>
            <StatCard
              title="Total de Solicitações"
              value={stats.totalRequests}
              icon="mdi:file-document-multiple"
              variant="blue"
            />
          </Col>
          <Col md={3}>
            <StatCard
              title="Pendentes"
              value={stats.pendingValidation + stats.pendingApproval}
              icon="mdi:clock-outline"
              variant="orange"
            />
          </Col>
          <Col md={3}>
            <StatCard
              title="Aprovados"
              value={stats.approved}
              icon="mdi:check-circle"
              variant="green"
            />
          </Col>
          <Col md={3}>
            <StatCard
              title="Este Mês"
              value={stats.requestsThisMonth}
              icon="mdi:calendar-month"
              variant="purple"
            />
          </Col>
        </Row>
      )}

      {/* Card principal */}
      <Card className="bg-transparent border-0">
        <Card.Header className="d-none"></Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')} style={{ marginBottom: '24px' }}>
              <Icon icon="mdi:alert-circle" className="me-2" />
              {error}
            </Alert>
          )}

          {/* Botões de Filtro (Abas) - Padrão Tickets */}
          <div className="d-flex gap-2 flex-wrap" style={{ marginBottom: '24px', gap: '8px' }}>
            <Button
              variant={activeView === 'pending' ? 'warning' : 'outline-warning'}
              onClick={() => handleViewChange('pending')}
              className="d-flex align-items-center gap-2"
            >
              <Icon icon="mdi:clock-outline" />
              Pendentes
              {tabStats.pending > 0 && (
                <Badge bg="light" text="dark">{tabStats.pending}</Badge>
              )}
            </Button>
            
            <Button
              variant={activeView === 'approved' ? 'success' : 'outline-success'}
              onClick={() => handleViewChange('approved')}
              className="d-flex align-items-center gap-2"
            >
              <Icon icon="mdi:check-circle" />
              Aprovadas
              {tabStats.approved > 0 && (
                <Badge bg="light" text="dark">{tabStats.approved}</Badge>
              )}
            </Button>
            
            <Button
              variant={activeView === 'rejected' ? 'danger' : 'outline-danger'}
              onClick={() => handleViewChange('rejected')}
              className="d-flex align-items-center gap-2"
            >
              <Icon icon="mdi:close-circle" />
              Rejeitadas
              {tabStats.rejected > 0 && (
                <Badge bg="light" text="dark">{tabStats.rejected}</Badge>
              )}
            </Button>
          </div>

          {/* Filtros (Data) */}
          <ApprovalFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            loading={loading}
          />

          {/* Busca - Separada como na tela de Tickets */}
          <div style={{ marginBottom: '24px' }}>
                <InputGroup>
              <InputGroup.Text>
                <Icon icon="mdi:magnify" />
              </InputGroup.Text>
                  <Form.Control
                    type="text"
                placeholder="Buscar por empresa, CNPJ, CPF, nome ou email do usuário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
                </InputGroup>
          </div>

          {/* Tabela */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3">Carregando solicitações...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-5">
              <Icon icon="mdi:file-document-outline" size={64} className="text-muted mb-3" />
              <h6 className="text-muted">
                {activeView === 'pending' && 'Nenhuma solicitação pendente encontrada'}
                {activeView === 'approved' && 'Nenhuma solicitação aprovada encontrada'}
                {activeView === 'rejected' && 'Nenhuma solicitação rejeitada encontrada'}
              </h6>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Empresa</th>
                      <th>CNPJ</th>
                      <th>Usuário</th>
                      <th>Status</th>
                      <th>Documentos</th>
                      <th>Criado em</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map((request) => (
                      <tr key={request.id}>
                        <td>
                          <div>
                            <div className="fw-semibold">{request.trading_name || request.company_name || 'N/A'}</div>
                            {request.trading_name && request.company_name && (
                              <small className="text-muted">{request.company_name}</small>
                            )}
                          </div>
                        </td>
                        <td>
                          <small>{formatCNPJ(request.state_registration) || 'N/A'}</small>
                        </td>
                        <td>
                          <div>
                            <div className="fw-semibold">{request.user_name}</div>
                            <small className="text-muted">{request.user_email}</small>
                          </div>
                        </td>
                        <td>{getStatusBadge(request.status)}</td>
                        <td>
                          <div>
                            <div>{request.documents_count || 0} total</div>
                            <div style={{ color: '#28a745', fontSize: '12px' }}>{request.approved_documents || 0} aprovados</div>
                            <div style={{ color: '#ffc107', fontSize: '12px' }}>{request.pending_documents || 0} pendentes</div>
                          </div>
                        </td>
                        <td>
                          <small className="text-muted">{formatDate(request.created_at)}</small>
                        </td>
                        <td className="text-center">
                            <Button
                            variant="primary"
                              size="sm"
                              onClick={() => handleViewRequest(request)}
                            title="Ver detalhes"
                            >
                              <Icon icon="mdi:eye" />
                            </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Paginação */}
              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} solicitações
                  </div>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={!pagination.hasPrev}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      <Icon icon="mdi:chevron-left" />
                    </Button>
                    <span style={{ fontSize: '14px', padding: '6px 12px' }}>
                      {pagination.page} de {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={!pagination.hasNext}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      <Icon icon="mdi:chevron-right" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Drawer de Detalhes */}
      <Drawer
        show={showDetailsDrawer}
        onHide={() => {
          setShowDetailsDrawer(false);
          setSelectedRequest(null);
        }}
        title="Detalhes da Solicitação"
        icon="mdi:file-document"
        size="xl"
        footer={
          selectedRequest?.responsible && (
            <div className="d-flex gap-2 justify-content-end w-100">
              <Drawer.Button
                variant="secondary"
                onClick={() => {
                  setShowDetailsDrawer(false);
                  setSelectedRequest(null);
                }}
              >
                Fechar
              </Drawer.Button>
              
              {/* Botão Reprovar */}
              {(selectedRequest.responsible.status === 'pending_validation' || 
                selectedRequest.responsible.status === 'pending_approval' ||
                selectedRequest.responsible.status === 'approved') && (
                <Drawer.Button
                  variant="danger"
                  onClick={() => {
                    setSelectedRequest({ responsible: selectedRequest.responsible });
                    setRejectionReason('');
                    setRejectionNotes('');
                    setShowRejectionModal(true);
                    setShowDetailsDrawer(false);
                  }}
                  icon="mdi:close-circle"
                >
                  Reprovar
                </Drawer.Button>
              )}
              
              {/* Botão Aprovar */}
              {(selectedRequest.responsible.status === 'pending_validation' || 
                selectedRequest.responsible.status === 'pending_approval' ||
                selectedRequest.responsible.status === 'rejected') && (
                <Drawer.Button
                  variant="success"
                  onClick={() => {
                    setSelectedRequest({ responsible: selectedRequest.responsible });
                    setApprovalNotes('');
                    setShowApprovalModal(true);
                    setShowDetailsDrawer(false);
                  }}
                  icon="mdi:check-circle"
                >
                  Aprovar
                </Drawer.Button>
              )}
            </div>
          )
        }
      >
        {loadingDetails ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Carregando detalhes...</p>
              </div>
        ) : selectedRequest ? (
          <>
              {/* Status Badge */}
            <Drawer.Section>
              <div className="d-flex justify-content-between align-items-center">
                <Drawer.Badge
                  variant={
                    selectedRequest.responsible.status === 'pending_validation' || 
                    selectedRequest.responsible.status === 'pending_approval' ? 'warning' :
                    selectedRequest.responsible.status === 'approved' ? 'success' : 'danger'
                  }
                >
                  {selectedRequest.responsible.status === 'pending_validation' ? 'Aguardando Validação' :
                   selectedRequest.responsible.status === 'pending_approval' ? 'Aguardando Aprovação' :
                     selectedRequest.responsible.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                </Drawer.Badge>
                <span style={{ fontSize: '12px', color: '#6c757d' }}>
                  Criado em {formatDate(selectedRequest.responsible.created_at)}
                  </span>
                </div>
            </Drawer.Section>
            <Drawer.Divider />

              {/* Dados da Empresa */}
            <Drawer.Section title="Dados da Empresa" icon="mdi:office-building">
              <Drawer.Row label="Razão Social" value={selectedRequest.responsible.company_name || 'N/A'} />
              <Drawer.Row label="Nome Fantasia" value={selectedRequest.responsible.trading_name || 'N/A'} />
              <Drawer.Row label="CNPJ/Inscrição Estadual" value={formatCNPJ(selectedRequest.responsible.state_registration) || 'N/A'} />
              <Drawer.Row label="CPF do Responsável" value={formatCPF(selectedRequest.responsible.responsible_cpf)} />
            </Drawer.Section>
            <Drawer.Divider />

              {/* Dados do Usuário */}
            <Drawer.Section title="Dados do Usuário" icon="mdi:account">
              <Drawer.Row label="Nome Completo" value={selectedRequest.responsible.user_name} />
              <Drawer.Row label="Email" value={selectedRequest.responsible.user_email} />
              <Drawer.Row label="Telefone" value={selectedRequest.responsible.responsible_phone || 'N/A'} />
              <Drawer.Row label="Função" value={selectedRequest.responsible.is_main_responsible ? 'Responsável Principal' : 'Responsável Secundário'} />
            </Drawer.Section>
            <Drawer.Divider />

              {/* Endereço */}
            <Drawer.Section title="Endereço" icon="mdi:map-marker">
              <Drawer.Row 
                label="Endereço" 
                value={`${selectedRequest.responsible.address_street}, ${selectedRequest.responsible.address_number}${selectedRequest.responsible.address_complement ? `, ${selectedRequest.responsible.address_complement}` : ''}`} 
              />
              <Drawer.Row label="Bairro" value={selectedRequest.responsible.address_neighborhood} />
              <Drawer.Row label="Cidade/Estado" value={`${selectedRequest.responsible.address_city} - ${selectedRequest.responsible.address_state}`} />
              <Drawer.Row label="CEP" value={selectedRequest.responsible.address_cep} />
            </Drawer.Section>
            <Drawer.Divider />

              {/* Documentos */}
            <Drawer.Section title={`Documentos do Responsável (${selectedRequest.documents?.length || 0})`} icon="mdi:file-document-multiple">
                    {!selectedRequest.documents || selectedRequest.documents.length === 0 ? (
                <Drawer.EmptyState 
                  icon="mdi:file-remove"
                  text="Nenhum documento foi enviado ainda."
                />
              ) : (
                <div>
                        {selectedRequest.documents.map((doc, index) => (
                    <div key={index} style={{ 
                            border: '1px solid #e9ecef', 
                            borderRadius: '8px', 
                            padding: '16px',
                      marginBottom: '12px',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                          }}>
                      <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#212529', textTransform: 'uppercase', marginBottom: '8px' }}>
                                  {doc.doc_type || 'Documento'}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>
                            <Icon icon="mdi:file" style={{ fontSize: '12px', marginRight: '4px' }} />
                                  {doc.file_name || 'Sem nome'}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                  <Icon icon="mdi:calendar" style={{ fontSize: '11px', marginRight: '4px' }} />
                                  Enviado em: {formatDate(doc.uploaded_at)}
                                </div>
                              </div>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => window.open(doc.url, '_blank')}
                          style={{ padding: '8px 16px', fontSize: '13px', marginLeft: '16px', whiteSpace: 'nowrap' }}
                        >
                          <Icon icon="mdi:eye" style={{ fontSize: '16px', marginRight: '6px' }} />
                                Visualizar
                              </Button>
                            </div>
                          </div>
                        ))}
                </div>
                    )}
            </Drawer.Section>

              {/* Histórico */}
              {selectedRequest.history && selectedRequest.history.length > 0 && (
              <>
                <Drawer.Divider />
                <Drawer.Section title="Histórico de Alterações" icon="mdi:history">
                  <div>
                      {selectedRequest.history.map((item, index) => (
                        <div key={index} className="d-flex mb-3">
                          <div className="flex-shrink-0">
                            <div style={{ 
                              width: '8px', 
                              height: '8px', 
                              backgroundColor: '#007bff', 
                              borderRadius: '50%', 
                              marginTop: '6px' 
                            }}></div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <div style={{ fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                              {item.action}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>
                              {item.admin_name} - {formatDate(item.created_at)}
                            </div>
                            {item.notes && (
                              <div style={{ fontSize: '11px', color: '#495057', marginTop: '2px' }}>
                                {item.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                </Drawer.Section>
              </>
            )}
          </>
        ) : (
          <Drawer.EmptyState 
            icon="mdi:alert"
            text="Não foi possível carregar os detalhes da solicitação."
          />
        )}
      </Drawer>

      {/* Modal de aprovação */}
      <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Aprovar Responsável</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: '14px', marginBottom: '16px' }}>
            Tem certeza que deseja aprovar o responsável <strong>{selectedRequest?.responsible?.trading_name || selectedRequest?.responsible?.company_name || 'N/A'}</strong>?
          </div>
          <Form.Group>
            <Form.Label style={{ fontSize: '12px', fontWeight: '500' }}>Observações (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder="Observações sobre a aprovação..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApprovalModal(false)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleConfirmApproval}>
            <Icon icon="mdi:check" className="me-1" />
            Aprovar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de reprovação */}
      <Modal show={showRejectionModal} onHide={() => setShowRejectionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reprovar Responsável</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: '14px', marginBottom: '16px' }}>
            Tem certeza que deseja reprovar o responsável <strong>{selectedRequest?.responsible?.trading_name || selectedRequest?.responsible?.company_name || 'N/A'}</strong>?
          </div>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '12px', fontWeight: '500' }}>
              Motivo da reprovação <span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Descreva o motivo da reprovação (mínimo 10 caracteres)..."
              isInvalid={rejectionReason.length > 0 && rejectionReason.length < 10}
            />
            {rejectionReason.length > 0 && rejectionReason.length < 10 && (
              <Form.Control.Feedback type="invalid">
                Motivo deve ter pelo menos 10 caracteres
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ fontSize: '12px', fontWeight: '500' }}>Observações adicionais (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              placeholder="Observações adicionais..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectionModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleConfirmRejection}
            disabled={rejectionReason.length < 10}
          >
            <Icon icon="mdi:close" className="me-1" />
            Reprovar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminApprovalsList;
